import React, { useState, useEffect } from 'react';
import CompassStateBar from './components/CompassStateBar';
import CompassOrientation from './components/CompassOrientation';
import ProtobufDevice from '../ble/protobufService/protobufDevice';
import { useProtobufDeviceContext } from '../ble/protobufService/ProtobufDeviceContext';
import protobuf from 'protobufjs';
import './compass.css';

// Default device name constant
const DEFAULT_DEVICE_NAME = "Naviguesser";

const Compass = () => {
  const [compassState, setCompassState] = useState(null);
  const [deviceStateCharacteristic, setDeviceStateCharacteristic] = useState(null);
  const [protobufRoot, setProtobufRoot] = useState(null);
  const [deviceConfigCharacteristic, setDeviceConfigCharacteristic] = useState(null);
  
  // Use the shared protobuf device context
  const { 
    isDeviceConnected, 
    protobufCharacteristics, 
    // eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: pre-existing write-only/unused binding in the legacy BLE+compass tree. Removing it is a behaviour question, not a lint fix; suppressed explicitly so no-unused-vars can stay an ERROR everywhere else.
    protobufSchemaRoot,
    protobufProtocolService 
  } = useProtobufDeviceContext();

  // Load protobuf definitions
  useEffect(() => {
    const loadProtobuf = async () => {
      const root = await protobuf.load('/compassData.proto');
      setProtobufRoot(root);
    };
    loadProtobuf();
  }, []);

  // Find device config characteristic
  useEffect(() => {
    if (protobufCharacteristics.length > 0) {
      const deviceConfigChar = protobufCharacteristics.find(char => char.protobufType === 'DeviceConfig');
      if (deviceConfigChar) {
        setDeviceConfigCharacteristic(deviceConfigChar);
      }
    }
  }, [protobufCharacteristics]);

  // Check cloud and initialize new devices
  useEffect(() => {
    if (isDeviceConnected && protobufRoot && deviceConfigCharacteristic) {
      initializeDeviceConfig();
    }
  }, [isDeviceConnected, protobufRoot, deviceConfigCharacteristic]);

  // Listen for device config changes and sync to cloud
  useEffect(() => {
    if (deviceConfigCharacteristic && deviceConfigCharacteristic.value) {
      // Sync config to cloud whenever it changes
      syncConfigToCloud(deviceConfigCharacteristic.value);
    }
  }, [deviceConfigCharacteristic?.value]);

  // Listen for characteristic pushed events to sync config changes to cloud
  useEffect(() => {
    if (protobufProtocolService) {
      const handleCharacteristicPushed = (event) => {
        if (event.uuid === deviceConfigCharacteristic?.uuid) {
          // Device config was pushed to device, sync to cloud
          if (deviceConfigCharacteristic?.value) {
            syncConfigToCloud(deviceConfigCharacteristic.value);
          }
        }
      };

      protobufProtocolService.on('characteristicPushed', handleCharacteristicPushed);

      return () => {
        protobufProtocolService.off('characteristicPushed', handleCharacteristicPushed);
      };
    }
  }, [protobufProtocolService, deviceConfigCharacteristic]);

  const initializeDeviceConfig = async () => {
    try {
      // Read current device config
      const currentConfig = deviceConfigCharacteristic.value;
      if (!currentConfig || !currentConfig.bleMac) {
        console.log('No device config or BLE MAC found');
        return;
      }

      // Check cloud for existing device
      const cloudConfig = await fetchCloudDeviceConfig(currentConfig, false);
      
      if (!cloudConfig) {
        // Device not found in cloud - assign new name and default config
        const deviceNumber = await getNextDeviceNumber();
        const newName = `${DEFAULT_DEVICE_NAME} ${deviceNumber.toString().padStart(3, '0')}`;
        
        const CompassDeviceConfig = protobufRoot.lookupType('compass.DeviceConfig');
        const defaultConfig = setDefaultValues({
          name: newName,
          bleMac: currentConfig.bleMac
        }, CompassDeviceConfig);

        // Update device with new config
        await protobufProtocolService.updateCharacteristicValue(
          protobufCharacteristics.indexOf(deviceConfigCharacteristic),
          'name',
          newName
        );
        await protobufProtocolService.pushChanges(
          protobufCharacteristics.indexOf(deviceConfigCharacteristic)
        );

        // Register in cloud
        await fetchCloudDeviceConfig(defaultConfig, true);
        
        console.log(`New device initialized: ${newName}`);
      } else {
        // Device exists in cloud - check if name needs reassignment
        if (cloudConfig.name === DEFAULT_DEVICE_NAME || 
            (cloudConfig.name.startsWith(DEFAULT_DEVICE_NAME + ' ') && 
             !/^\d{3}$/.test(cloudConfig.name.substring(DEFAULT_DEVICE_NAME.length + 1)))) {
          // Device has default name or invalid format, reassign to numbered format
          const deviceNumber = await getNextDeviceNumber();
          const newName = `${DEFAULT_DEVICE_NAME} ${deviceNumber.toString().padStart(3, '0')}`;
          
          // Update device with new name
          await protobufProtocolService.updateCharacteristicValue(
            protobufCharacteristics.indexOf(deviceConfigCharacteristic),
            'name',
            newName
          );
          await protobufProtocolService.pushChanges(
            protobufCharacteristics.indexOf(deviceConfigCharacteristic)
          );

          // Update cloud config
          const updatedConfig = { ...cloudConfig, name: newName };
          await fetchCloudDeviceConfig(updatedConfig, true);
          
          console.log(`Device name reassigned: ${newName}`);
        }
      }
    } catch (error) {
      console.error('Error initializing device config:', error);
    }
  };

  const setDefaultValues = (obj, type) => {
    const defaultValues = {};
    const fields = type.fields;
    for (const key in fields) {
      const fieldType = fields[key]?.resolvedType;
      if (fieldType && fieldType instanceof protobuf.Type) {
        defaultValues[key] = setDefaultValues({}, fieldType);
      } else {
        switch (fields[key].type) {
          case 'bool':
            defaultValues[key] = false;
            break;
          case 'int32':
          case 'uint32':
          case 'float':
          case 'double':
            defaultValues[key] = 0;
            break;
          case 'string':
            defaultValues[key] = '';
            break;
          case 'GPSModel':
            defaultValues[key] = 0;
            break;
          default:
            defaultValues[key] = null;
        }
      }
    }
    return { ...defaultValues, ...obj };
  };

  const fetchCloudDeviceConfig = async (config, overwrite = false) => {
    try {
      const response = await fetch('https://naviguesser.app/device_config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceConfig: config, overwrite }),
      });

      if (!response.ok) {
        if (response.status === 404) return null; // Device not found
        throw new Error(`Error fetching cloud config: ${response.statusText}`);
      }

      const cloudResponse = await response.json();
      return cloudResponse.deviceConfig;
    } catch (error) {
      console.error('Failed to fetch cloud config:', error);
      return null;
    }
  };

  const getNextDeviceNumber = async () => {
    try {
      // Get all existing devices
      const response = await fetch('https://naviguesser.app/list_devices', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.devices) {
          console.log('Found devices:', result.devices.map(d => d.name));
          
          // Count total devices and find existing Naviguesser numbers
          
          const existingNumbers = new Set();
          
          result.devices.forEach(device => {
            if (device.name && device.name.startsWith(DEFAULT_DEVICE_NAME + ' ')) {
              const numberStr = device.name.substring(DEFAULT_DEVICE_NAME.length + 1);
              const number = parseInt(numberStr, 10);
              console.log(`Device: ${device.name}, Extracted number: ${numberStr}, Parsed: ${number}`);
              if (!isNaN(number)) {
                existingNumbers.add(number);
              }
            }
          });
          
          // Reset numbering - start from 1
          let candidateNumber = 1;
          console.log(`Reset numbering: starting from ${candidateNumber}`);
          
          // Increment until we find a number that doesn't conflict
          while (existingNumbers.has(candidateNumber)) {
            candidateNumber++;
            console.log(`Number ${candidateNumber - 1} conflicts, trying ${candidateNumber}`);
          }
          
          console.log(`Final assigned number: ${candidateNumber}`);
          return candidateNumber;
        }
      }
    } catch (error) {
      console.error('Failed to get next device number:', error);
    }
    
    // Fallback: return a random number if we can't query existing devices
    const fallbackNumber = Math.floor(Math.random() * 1000) + 1;
    console.log(`Using fallback number: ${fallbackNumber}`);
    return fallbackNumber;
  };

  const [lastSyncedConfig, setLastSyncedConfig] = useState(null);

  const syncConfigToCloud = async (config) => {
    // Prevent duplicate syncs by checking if config has actually changed
    const configString = JSON.stringify(config);
    if (lastSyncedConfig === configString) {
      return;
    }
    
    try {
      await fetchCloudDeviceConfig(config, true);
      setLastSyncedConfig(configString);
      console.log('Device config synced to cloud');
    } catch (error) {
      console.error('Failed to sync config to cloud:', error);
    }
  };

  const handleCharacteristicsChange = (characteristics) => {
    const deviceStateChar = characteristics.find(char => char.protobufType === 'DeviceState');
    if (deviceStateChar) {
      setDeviceStateCharacteristic(deviceStateChar);
      if (deviceStateChar.value) {
        setCompassState(deviceStateChar.value);
      }
    }
  };

  // Subscribe to characteristic value changes
  useEffect(() => {
    if (deviceStateCharacteristic?.BLEcharacteristic) {
      const handleValueChange = (event) => {
        const rawValue = event.target.value;
        const newValue = deviceStateCharacteristic.messageType.decode(new Uint8Array(rawValue.buffer));
        setCompassState(newValue);
      };

      deviceStateCharacteristic.BLEcharacteristic.addEventListener('characteristicvaluechanged', handleValueChange);

      return () => {
        deviceStateCharacteristic.BLEcharacteristic.removeEventListener('characteristicvaluechanged', handleValueChange);
      };
    }
  }, [deviceStateCharacteristic]);

  return (
    <div className="compass-page">
      <div className="compass-layout">
        <div className="left-column">
          <CompassStateBar compassState={compassState} />
          <CompassOrientation compassState={compassState} />
        </div>
        <div className="right-column">
          <ProtobufDevice 
            onCharacteristicsChange={handleCharacteristicsChange}
            onConfigUpdate={(config) => syncConfigToCloud(config)}
          />
        </div>
      </div>
    </div>
  );
};

export default Compass; 